import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Mail, XCircle, DollarSign, Download } from 'lucide-react';
import { Registration } from '@/modules/admin/hooks/useRegistrations';

interface RegistrationDetailsModalProps {
  registration: Registration | null;
  open: boolean;
  onClose: () => void;
  onResendEmail: () => void;
  onCancel: () => void;
  onRefund: () => void;
}

export function RegistrationDetailsModal({
  registration,
  open,
  onClose,
  onResendEmail,
  onCancel,
  onRefund
}: RegistrationDetailsModalProps) {
  if (!registration) return null;

  const { registrant_data, pricing_breakdown, status, payment_status } = registration;

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-500/10 text-green-700 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400';
      case 'cancelled':
        return 'bg-red-500/10 text-red-700 dark:text-red-400';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registration Details</DialogTitle>
        </DialogHeader>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-2">
          <Badge className={getStatusColor(status)}>{status}</Badge>
          <Badge variant="outline">{payment_status}</Badge>
          <Badge variant="outline">{registration.registration_type}</Badge>
        </div>

        {/* Contact Information */}
        <div>
          <h3 className="font-semibold mb-3">Contact Information</h3>
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <span className="text-muted-foreground">Name:</span>
              <span className="col-span-2 font-medium">
                {registrant_data.contact_name || 'N/A'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <span className="text-muted-foreground">Email:</span>
              <span className="col-span-2">{registrant_data.contact_email || 'N/A'}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <span className="text-muted-foreground">Phone:</span>
              <span className="col-span-2">{registrant_data.contact_phone || 'N/A'}</span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Registration Details */}
        <div>
          <h3 className="font-semibold mb-3">Registration Details</h3>
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <span className="text-muted-foreground">Type:</span>
              <span className="col-span-2 font-medium">
                {registration.registration_type}
              </span>
            </div>
            {registrant_data.pricing_tier && (
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Tier:</span>
                <span className="col-span-2">{registrant_data.pricing_tier}</span>
              </div>
            )}
            {registrant_data.division && (
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Division:</span>
                <span className="col-span-2">{registrant_data.division}</span>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Pricing Breakdown */}
        <div>
          <h3 className="font-semibold mb-3">Pricing Breakdown</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Base Price</span>
              <span className="font-medium">
                {formatCurrency(pricing_breakdown?.base_price || 0)}
              </span>
            </div>

            {registrant_data.extras_selected && registrant_data.extras_selected.length > 0 && (
              <>
                <div className="font-medium text-muted-foreground mt-3">Extras</div>
                {registrant_data.extras_selected.map((extra, idx) => (
                  <div key={idx} className="flex justify-between pl-4">
                    <span>{extra.name} (x{extra.quantity})</span>
                    <span>{formatCurrency(extra.total)}</span>
                  </div>
                ))}
              </>
            )}

            {registrant_data.spectator_tickets && registrant_data.spectator_tickets.length > 0 && (
              <>
                <div className="font-medium text-muted-foreground mt-3">Spectator Tickets</div>
                {registrant_data.spectator_tickets.map((ticket, idx) => (
                  <div key={idx} className="flex justify-between pl-4">
                    <span>{ticket.name} (x{ticket.quantity})</span>
                    <span>{formatCurrency(ticket.total)}</span>
                  </div>
                ))}
              </>
            )}

            {registrant_data.special_requests?.requested && (
              <div className="flex justify-between">
                <span>Special Requests Fee</span>
                <span>{formatCurrency(registrant_data.special_requests.fee)}</span>
              </div>
            )}

            {registrant_data.discount_code && (
              <div className="flex justify-between text-green-600">
                <span>Discount ({registrant_data.discount_code})</span>
                <span>-{formatCurrency(pricing_breakdown?.discount_amount || 0)}</span>
              </div>
            )}

            <Separator className="my-3" />

            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span>{formatCurrency(pricing_breakdown?.total || 0)}</span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Payment Information */}
        {registration.stripe_payment_intent_id && (
          <div>
            <h3 className="font-semibold mb-3">Payment Information</h3>
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Status:</span>
                <span className="col-span-2 font-medium">{payment_status}</span>
              </div>
              {registration.payment_method && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-muted-foreground">Method:</span>
                  <span className="col-span-2">{registration.payment_method}</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Payment ID:</span>
                <span className="col-span-2 font-mono text-xs">
                  {registration.stripe_payment_intent_id}
                </span>
              </div>
            </div>
          </div>
        )}

        {registrant_data.special_requests?.requested && (
          <>
            <Separator />
            <div>
              <h3 className="font-semibold mb-3">Special Requests</h3>
              <p className="text-sm text-muted-foreground">
                {registrant_data.special_requests.details}
              </p>
            </div>
          </>
        )}

        <Separator />

        {/* Activity Timeline */}
        <div>
          <h3 className="font-semibold mb-3">Activity Timeline</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
              <div className="flex-1">
                <p className="font-medium">Registration Created</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(registration.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-4">
          <Button onClick={onResendEmail} variant="outline" size="sm">
            <Mail className="h-4 w-4 mr-2" />
            Resend Email
          </Button>
          {status !== 'cancelled' && (
            <Button onClick={onCancel} variant="outline" size="sm">
              <XCircle className="h-4 w-4 mr-2" />
              Cancel Registration
            </Button>
          )}
          {payment_status === 'paid' && (
            <Button onClick={onRefund} variant="outline" size="sm">
              <DollarSign className="h-4 w-4 mr-2" />
              Refund Payment
            </Button>
          )}
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Download Receipt
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
